import React from 'react';
import { useTranslation } from 'react-i18next';
import type { EpgChannel, EpgProgram } from '@jwp/ott-common/types/epg';
import { formatVideoScheduleTime } from '@jwp/ott-common/src/utils/formatting';
import Image from '@jwp/ott-ui-react/src/components/Image/Image';
import Button from '@jwp/ott-ui-react/src/components/Button/Button';
import Icon from '@jwp/ott-ui-react/src/components/Icon/Icon';
import { differenceInMilliseconds } from 'date-fns';
import { programIsFullyWatchable, programIsLive } from '@jwp/ott-common/src/utils/epg';
import classNames from 'classnames';
import Play from '@jwp/ott-theme/assets/icons/play.svg?react';
import Replay from '@jwp/ott-theme/assets/icons/replay.svg?react';
import Live from '@jwp/ott-theme/assets/icons/live_24dp.svg?react';

import styles from './EpgTimeline.module.scss';

type Props = {
  channel: EpgChannel;
  programsRef: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onProgramClick: (program: EpgProgram, fromBeginning?: boolean) => void;
};

const EpgTimeline: React.FC<Props> = ({ channel, programsRef, onProgramClick }: Props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['epg', 'video']);

  const programs = channel.programs;
  const liveProgram = programs.find(programIsLive);

  const setProgramItemRef = (index: number) => (el: HTMLDivElement) => {
    programsRef.current[index] = el;
  };

  const progress = Math.min(
    1,
    Math.max(
      0,
      differenceInMilliseconds(Date.now(), liveProgram?.startTime ?? 0) / differenceInMilliseconds(liveProgram?.endTime ?? 0, liveProgram?.startTime ?? 0),
    ),
  );

  if (programs.length === 0) {
    return <p>{t('not_available')}</p>;
  }

  return (
    <div className={styles.schedule} role="grid" aria-label={t('guide')}>
      <div role="row">
        {programs.map((program, index) => {
          const isLive = liveProgram === program;
          const isFullyWatchable = programIsFullyWatchable(program, channel.catchupHours);
          const programInfo = (
            <>
              <h2 className={styles.title}>{program.title}</h2>
              <p className={styles.meta}>{formatVideoScheduleTime(language, new Date(program.startTime), new Date(program.endTime))}</p>
            </>
          );

          return (
            <div
              className={classNames(styles.program, { [styles.disabled]: !isLive && !isFullyWatchable })}
              role="gridcell"
              aria-expanded={isLive}
              key={program.id}
              ref={setProgramItemRef(index)}
            >
              {isLive ? (
                <div className={styles.card}>
                  <div className={styles.poster} onClick={() => onProgramClick(program)}>
                    <Image image={program.cardImage || channel.backgroundImage} alt="" className={styles.posterImage} />
                    <div className={styles.liveTag}>
                      <Icon icon={Live} /> {t('common:live')}
                    </div>
                    {progress > 0 && (
                      <div className={styles.progressContainer}>
                        <div className={styles.progressBar} style={{ width: `${Math.round(progress * 100)}%` }} />
                      </div>
                    )}
                  </div>
                  {programInfo}
                  <div className={styles.buttonContainer}>
                    <Button
                      color="primary"
                      variant="contained"
                      startIcon={<Icon icon={Play} />}
                      onClick={() => onProgramClick(program)}
                      className={styles.button}
                      label={t('video:start_watching')}
                      fullWidth={true}
                    />
                    {isFullyWatchable && (
                      <Button
                        startIcon={<Icon icon={Replay} />}
                        onClick={() => onProgramClick(program, true)}
                        className={styles.button}
                        label={t('start_from_beginning')}
                        fullWidth={true}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <button tabIndex={0} onClick={() => isFullyWatchable && onProgramClick(program)} className={styles.card}>
                  {programInfo}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EpgTimeline;
